/** @format */

// Product catalog page, webshop style (read-only, accessible by all users)
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { api } from '../../../../api.ts';
import { selectAuth } from '../../../../redux/authSlice';
import { ThemeProvider } from '../../../../theme';
import CatalogCard from './catalog/CatalogCard';
import CatalogFilters, { CatalogFilterBar } from './catalog/CatalogFilters';
import CatalogProductSheet from './catalog/CatalogProductSheet';
import CatalogSearch from './catalog/CatalogSearch';
import {
  forgetProduct,
  getDetail,
  getPrice,
  loadTypologyDetails,
  requestDetail,
  requestPrice,
  retryIndex,
  runPool,
  useCatalogIndex,
  useCatalogRefs,
  useDetailsSchema,
  useInactiveProducts,
  useSearchIds,
  useTypologyDetails,
} from './catalog/catalogData';
import {
  DOCS_OPTIONS,
  EMPTY_FILTERS,
  SEARCH_DEBOUNCE_MS,
  SORT_OPTIONS,
  TYPOLOGY_CRITERIA,
  buildCatalogCsv,
  collectCriterionValues,
  computeCatalogView,
  getBrandId,
  getSuggestions,
  isSearchActive,
  normalizeText,
  selectTypology,
  sortProducts,
  toggleBrand,
  toggleCriterion,
} from './catalog/catalogLogic';
import ProductForm from './ProductForm';

const RENDER_STEP = 24;
const PHONE_MAX = 700;
const TABLET_MAX = 1100;

function useElementWidth(ref) {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return width;
}

function downloadCsv(content, filename) {
  const url = URL.createObjectURL(
    new Blob([content], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function ProductCatalogPage() {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const sentinelRef = useRef(null);
  const width = useElementWidth(rootRef);
  const isPhone = width < PHONE_MAX;
  const isTablet = !isPhone && width < TABLET_MAX;
  const isDesktop = !isPhone && !isTablet;

  const refs = useCatalogRefs();
  const index = useCatalogIndex(refs);
  const ready = index.status === 'ready';

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rawSearch, setRawSearch] = useState('');
  const [sort, setSort] = useState('relevance');
  const [openProduct, setOpenProduct] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [renderCount, setRenderCount] = useState(RENDER_STEP);
  const [exporting, setExporting] = useState(null);

  // Admin edit (existing form)
  const [editingProduct, setEditingProduct] = useState(null);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const { user } = useSelector(selectAuth) || {};
  const canEdit = user?.is_superuser || user?.is_product_manager;

  const typologiesBySlug = useMemo(
    () => Object.fromEntries(refs.typologies.map((t) => [t.slug, t])),
    [refs.typologies],
  );
  const brandsById = useMemo(
    () => Object.fromEntries(refs.brands.map((b) => [b.id, b])),
    [refs.brands],
  );
  const selectedTypology = typologiesBySlug[filters.typology] || null;
  const schema = useDetailsSchema(filters.typology);

  useEffect(() => {
    const handler = setTimeout(
      () => setFilters((f) => ({ ...f, search: rawSearch })),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handler);
  }, [rawSearch]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName;
      const typing =
        tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape' && isDesktop && !typing) setOpenProduct(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop]);

  const criteriaStore = useTypologyDetails(
    filters.typology,
    ready && Boolean(filters.typology),
  );
  const criteriaStatus = criteriaStore[filters.typology];
  const criteriaReady = criteriaStatus?.status === 'complete';
  const inactive = useInactiveProducts(filters.showInactive);
  const searchActive = isSearchActive(filters.search);
  const searchIds = useSearchIds(filters.search, ready && searchActive);

  const products = useMemo(() => {
    const base = ready ? index.items : index.firstPage;
    // Criteria need the full products of the typology, once loaded.
    const withDetails = criteriaReady
      ? base.map((p) =>
          p.typology?.slug === filters.typology ? getDetail(p.id) || p : p,
        )
      : base;
    return filters.showInactive
      ? [...withDetails, ...inactive.items]
      : withDetails;
  }, [
    ready,
    index,
    criteriaReady,
    filters.typology,
    filters.showInactive,
    inactive.items,
  ]);
  const view = useMemo(
    () => computeCatalogView(products, filters, brandsById, searchIds?.ids),
    [products, filters, brandsById, searchIds],
  );
  const items = useMemo(
    () => sortProducts(view.items, sort, filters.search, brandsById),
    [view.items, sort, filters.search, brandsById],
  );

  useEffect(() => setRenderCount(RENDER_STEP), [filters, sort]);
  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === 'undefined')
      return undefined;
    const observer = new IntersectionObserver(
      ([entry]) =>
        entry.isIntersecting && setRenderCount((n) => n + RENDER_STEP),
      { rootMargin: '600px' },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [items.length > renderCount]);

  const criteria = useMemo(() => {
    const ofTypology = products.filter(
      (p) => p.typology?.slug === filters.typology,
    );
    return (TYPOLOGY_CRITERIA[filters.typology] || []).map((criterion) => ({
      criterion,
      values: collectCriterionValues(
        ofTypology,
        criterion,
        schema?.choices?.[criterion.field],
      ),
    }));
  }, [products, filters.typology, schema]);

  const searchPending =
    normalizeText(rawSearch) !== normalizeText(filters.search) ||
    (searchActive && (!ready || searchIds?.status === 'loading'));

  const suggestions = useMemo(
    () =>
      getSuggestions(filters.search, {
        products: items,
        brands: refs.brands.filter((b) => view.brandCounts[b.id] > 0),
        typologies: refs.typologies.filter(
          (t) => view.typologyCounts[t.slug] > 0,
        ),
        brandsById,
      }),
    [filters.search, items, refs, view, brandsById],
  );

  const position = openProduct
    ? items.findIndex((p) => p.id === openProduct.id) + 1
    : 0;
  const openAt = (i) => setOpenProduct(items[i] || null);

  const handlePick = (s) => {
    if (s.kind === 'brand') {
      if (!filters.brands.includes(s.value.id))
        setFilters((f) => toggleBrand(f, s.value.id));
    } else if (s.kind === 'typology') {
      setFilters((f) => selectTypology(f, s.value));
    } else {
      setOpenProduct(s.value);
    }
  };

  const clearAll = () => {
    setRawSearch('');
    setFilters((f) => ({
      ...f,
      subCategory: null,
      brands: [],
      criteria: {},
      docs: [],
      search: '',
    }));
  };

  const subCategoryName = refs.subCategories.find(
    (sc) => sc.slug === filters.subCategory,
  )?.name;
  const activeChips = [
    ...(subCategoryName
      ? [
          {
            key: 'sub-category',
            label: `Sous-catégorie : ${subCategoryName}`,
            onDelete: () => setFilters((f) => ({ ...f, subCategory: null })),
          },
        ]
      : []),
    ...filters.brands.map((id) => ({
      key: `brand-${id}`,
      label: `Marque : ${brandsById[id]?.name || '—'}`,
      onDelete: () =>
        setFilters((f) => ({ ...f, brands: f.brands.filter((b) => b !== id) })),
    })),
    ...criteria.flatMap(({ criterion, values }) =>
      (filters.criteria[criterion.field] || []).map((key) => ({
        key: `${criterion.field}-${key}`,
        label: `${criterion.label} : ${
          values.find((v) => v.key === key)?.label || key
        }`,
        onDelete: () =>
          setFilters((f) => toggleCriterion(f, criterion.field, key)),
      })),
    ),
    ...filters.docs.map((key) => ({
      key: `doc-${key}`,
      label: DOCS_OPTIONS.find((d) => d.key === key)?.label,
      onDelete: () =>
        setFilters((f) => ({ ...f, docs: f.docs.filter((d) => d !== key) })),
    })),
    ...(isSearchActive(filters.search)
      ? [
          {
            key: 'search',
            label: `Recherche : ${filters.search.trim()}`,
            onDelete: () => setRawSearch(''),
          },
        ]
      : []),
  ];

  const handleExport = async () => {
    // The export needs the full product (reference, details, documents) and the price.
    const total = items.length;
    let done = 0;
    setExporting({ done, total });
    await runPool(items, 6, async (p) => {
      await requestDetail(p.id);
      if (p.is_active) await requestPrice(p.id);
      done += 1;
      setExporting({ done, total });
    });
    const prices = Object.fromEntries(
      items.map((p) => [p.id, getPrice(p.id)?.value ?? null]),
    );
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(
      buildCatalogCsv(
        items.map((p) => getDetail(p.id) || p),
        { prices, brandsById },
      ),
      `catalogue-produits-${
        filters.typology || 'toutes-typologies'
      }-${date}.csv`,
    );
    setExporting(null);
  };

  const handleEdit = async (product) => {
    setEditError(null);
    setFormSubmitLoading(true);
    try {
      const response = await api.get(
        `/products/v2/admin-products/${product.id}/`,
      );
      setEditingProduct(response.data);
    } catch (err) {
      setEditError('Erreur lors du chargement des détails du produit.');
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const handleSaveProduct = async (
    productData,
    imageFile,
    productId = null,
    dataSheetFormData = null,
  ) => {
    setFormSubmitLoading(true);
    try {
      let savedProduct = productId
        ? (
            await api.patch(
              `/products/v2/admin-products/${productId}/`,
              {},
              productData,
            )
          ).data
        : (await api.post('/products/v2/admin-products/', {}, productData))
            .data;

      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        const imageResponse = await api.patch(
          `/products/v2/admin-products/${savedProduct.id}/`,
          {},
          imageFormData,
          {
            'Content-Type': 'multipart/form-data',
          },
        );
        savedProduct = { ...savedProduct, ...imageResponse.data };
      }

      if (
        dataSheetFormData &&
        Array.from(dataSheetFormData.entries()).length > 0
      ) {
        const uploadResponse = await api.post(
          `/products/v2/admin-products/${savedProduct.id}/upload_data_sheets/`,
          {},
          dataSheetFormData,
          { 'Content-Type': 'multipart/form-data' },
        );
        savedProduct = uploadResponse.data;
      }

      setEditingProduct(null);
      setOpenProduct(null);
      // shortcut: a renamed or new product shows in the light index after a page reload.
      forgetProduct(savedProduct);
      return savedProduct;
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const handleDuplicateProduct = async (product) => {
    setFormSubmitLoading(true);
    try {
      await api.post(
        `/products/v2/admin-products/${product.id}/duplicate/`,
        {},
      );
    } catch (err) {
      setEditError('Erreur lors de la duplication du produit.');
      console.error('Duplicate product error:', err);
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const handleSyncProductToOdoo = async (product) => {
    setFormSubmitLoading(true);
    try {
      const response = await api.post(
        `/products/v2/admin-products/${product.id}/sync_to_odoo/`,
        {},
        {},
      );
      setEditingProduct((prev) =>
        prev && prev.id === product.id
          ? {
              ...prev,
              odoo_id: response.data.odoo_id,
              odoo_bom_id: response.data.odoo_bom_id,
            }
          : prev,
      );
    } catch (err) {
      setEditError(
        err.response?.data?.error ||
          'Erreur lors de la synchronisation avec Odoo.',
      );
      console.error('Sync product to Odoo error:', err);
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const filtersPanel = (
    <CatalogFilters
      filters={filters}
      onChange={setFilters}
      typologies={refs.typologies}
      view={view}
      ready={ready}
      inactiveLoading={filters.showInactive && inactive.status === 'loading'}
    />
  );

  const sheet = openProduct && (
    <CatalogProductSheet
      product={openProduct}
      position={position}
      total={items.length}
      onPrev={() => openAt(position - 2)}
      onNext={() => openAt(position)}
      onClose={() => setOpenProduct(null)}
      brand={brandsById[getBrandId(openProduct)] || openProduct.brand || null}
      typology={typologiesBySlug[openProduct.typology?.slug]}
      onOpenProduct={(p) =>
        setOpenProduct(products.find((x) => x.id === p.id) || p)
      }
      onEdit={canEdit ? handleEdit : undefined}
    />
  );

  let columns = 4;
  if (isPhone) columns = width < 520 ? 1 : 2;
  else if (isTablet) columns = 2;
  else if (openProduct) columns = 3;
  const filterCount =
    (filters.typology ? 1 : 0) + (filters.showInactive ? 1 : 0);

  return (
    <ThemeProvider>
      <Box
        ref={rootRef}
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          pb: isPhone ? 10 : 4,
          overflowX: 'clip',
        }}
      >
        {(refs.error || editError) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              refs.error ? (
                <Button
                  color="inherit"
                  onClick={() => window.location.reload()}
                >
                  Réessayer
                </Button>
              ) : null
            }
            onClose={editError ? () => setEditError(null) : undefined}
          >
            {refs.error || editError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {!isPhone && (
            <Box
              sx={{
                width: isTablet ? 232 : 256,
                flexShrink: 0,
                position: 'sticky',
                top: 16,
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
                pr: 1,
              }}
            >
              {filtersPanel}
            </Box>
          )}

          <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: isPhone ? 'wrap' : 'nowrap',
                mt: 2,
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontSize: isPhone ? 22 : 28,
                  fontWeight: 700,
                  flex: 1,
                  minWidth: 0,
                }}
                noWrap
              >
                {selectedTypology?.name || 'Catalogue des produits'}
              </Typography>
              {isPhone && (
                <Badge badgeContent={filterCount} color="primary">
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFiltersOpen(true)}
                    sx={{ minHeight: 44, textTransform: 'none', fontSize: 15 }}
                  >
                    Typologies
                  </Button>
                </Badge>
              )}
              <CatalogSearch
                ref={searchRef}
                value={rawSearch}
                onChange={setRawSearch}
                pending={searchPending}
                suggestions={suggestions}
                onPick={handlePick}
                fullWidth={isPhone}
              />
            </Box>

            <CatalogFilterBar
              filters={filters}
              onChange={setFilters}
              subCategories={refs.subCategories}
              brands={refs.brands}
              view={view}
              criteria={criteria}
              ready={ready}
              brandsStatus={index.brandsStatus}
              onRetryBrands={retryIndex}
              criteriaStatus={criteriaStatus}
              onRetryCriteria={() => loadTypologyDetails(filters.typology)}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                mt: 2,
              }}
            >
              <Typography sx={{ fontWeight: 600, mr: 1 }} aria-live="polite">
                {ready
                  ? `${items.length} fiche${items.length > 1 ? 's' : ''}`
                  : '— fiches'}
              </Typography>
              {activeChips.map((c) => (
                <Chip
                  key={c.key}
                  label={c.label}
                  onDelete={c.onDelete}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
              {activeChips.length > 0 && (
                <Button
                  onClick={clearAll}
                  sx={{ minHeight: 44, textTransform: 'none', fontSize: 15 }}
                >
                  Tout effacer
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <TextField
                select
                size="small"
                label="Trier par"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                sx={{ minWidth: 160 }}
                InputProps={{ sx: { minHeight: 44 } }}
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={handleExport}
                disabled={!ready || Boolean(exporting) || !items.length}
                title={
                  !ready
                    ? 'Disponible quand le catalogue est chargé'
                    : undefined
                }
                sx={{ minHeight: 44, textTransform: 'none', fontSize: 15 }}
              >
                {exporting
                  ? `Préparation de l'export… ${exporting.done}/${exporting.total}`
                  : 'Exporter'}
              </Button>
            </Box>

            {index.status === 'error' && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                action={
                  <Button color="inherit" onClick={retryIndex}>
                    Réessayer
                  </Button>
                }
              >
                {index.error} Les fiches déjà affichées restent visibles.
              </Alert>
            )}
            {filters.showInactive && inactive.status === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Le chargement des fiches inactives a échoué.
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: 2,
                mt: 2,
              }}
            >
              {items.slice(0, renderCount).map((p) => (
                <CatalogCard
                  key={p.id}
                  product={p}
                  brand={brandsById[getBrandId(p)] || null}
                  typology={typologiesBySlug[p.typology?.slug]}
                  selected={openProduct?.id === p.id}
                  onOpen={setOpenProduct}
                />
              ))}
              {!ready &&
                items.length === 0 &&
                Array.from({ length: columns * 2 }, (_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={300}
                    sx={{ borderRadius: 3 }}
                  />
                ))}
            </Box>
            {items.length > renderCount && (
              <Box ref={sentinelRef} sx={{ height: 1 }} />
            )}

            {ready && items.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  Aucune fiche ne correspond à ces filtres.
                </Typography>
                {activeChips.length > 0 && (
                  <Button
                    onClick={clearAll}
                    sx={{
                      mt: 1,
                      minHeight: 44,
                      textTransform: 'none',
                      fontSize: 15,
                    }}
                  >
                    Tout effacer
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {isDesktop && openProduct && (
            <Box
              component="aside"
              aria-label="Fiche produit"
              sx={{
                width: 400,
                flexShrink: 0,
                position: 'sticky',
                top: 16,
                height: 'calc(100vh - 32px)',
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {sheet}
            </Box>
          )}
        </Box>

        {!isDesktop && (
          <Drawer
            anchor="right"
            open={Boolean(openProduct)}
            onClose={() => setOpenProduct(null)}
            PaperProps={{ sx: { width: isPhone ? '100%' : 400 } }}
          >
            {sheet}
          </Drawer>
        )}

        {isPhone && (
          <Drawer
            anchor="left"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{ sx: { width: '100%', maxWidth: 420 } }}
          >
            <Box sx={{ px: 2, pb: 2, flex: 1, overflowY: 'auto' }}>
              {filtersPanel}
            </Box>
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                p: 2,
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={() => setFiltersOpen(false)}
                sx={{ minHeight: 48 }}
              >
                Voir {items.length} fiche{items.length > 1 ? 's' : ''}
                {ready ? '' : ' (chargement…)'}
              </Button>
            </Box>
          </Drawer>
        )}

        {editingProduct && (
          <ProductForm
            open
            onClose={() => setEditingProduct(null)}
            onSave={handleSaveProduct}
            product={editingProduct}
            isLoading={formSubmitLoading}
            readOnly={false}
            handleDuplicateProduct={handleDuplicateProduct}
            handleSyncProductToOdoo={handleSyncProductToOdoo}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export default ProductCatalogPage;
